"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiSelect = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const should_forward_prop_1 = __importDefault(require("@styled-system/should-forward-prop"));
const react_1 = require("react");
const react_select_1 = __importDefault(require("react-select"));
const constants_1 = require("../../constants");
const styles_1 = require("../../constants/styles");
const useValidate_1 = require("../../hooks/useValidate");
const translate_1 = require("../../translate");
const SelectedIcon_1 = require("../Dropdown/SelectedIcon");
const ErrorMessage_1 = require("../ErrorMessage");
const Form_1 = require("../Form");
const Icon_1 = require("../Icon");
const SINGLE_LINE = true;
const createStyles = ({ invalid, }) => {
    return {
        container: (base) => ({
            ...base,
        }),
        control: (base, props) => ({
            ...base,
            ...(0, styles_1.comboboxInputStyles)({ invalid }),
            color: constants_1.Color.DARKEST_MEDIUM_GREY,
            paddingBottom: 4.4,
            paddingRight: 32,
            paddingTop: 4.4,
            // `react-select` removes pointer events for disabled inputs,
            // but they are needed for displaying the `not-allowed` cursor:
            ...(props.isDisabled && { ...styles_1.DISABLED_STYLES, pointerEvents: 'all' }),
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: constants_1.Color.BLACK,
        }),
        indicatorsContainer: () => ({
            display: 'none',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            paddingBottom: 3.6,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 3.6,
            /*
             * Avoid unattractively wrapping the empty input to a new line if simultaneously:
             *  - The menu is closed
             *  - There is at least one value selected
             *
             * Note that we're using `position: absolute` instead of `display: none`, as it makes
             * the entire multi-select non-interactive for some reason.
             */
            ':has([aria-expanded="false"]):has([class*="multiValue"])': {
                position: 'absolute',
            },
        }),
        menu: (base) => {
            // Omitting `top` as it doesn't work with the `menuPlacement="auto"` prop:
            const { top: _, ...styles } = (0, styles_1.dropdownItemsStyles)();
            return {
                ...base,
                ...styles,
            };
        },
        menuList: (base) => ({
            ...base,
            padding: 0,
        }),
        menuPortal: (base) => ({
            ...base,
            marginTop: 4,
            zIndex: constants_1.theme.zIndex.popover,
        }),
        multiValue: (base, props) => ({
            ...base,
            backgroundColor: constants_1.Color.LIGHTEST_BLUE,
            borderColor: constants_1.Color.TRANSPARENT_BLUE,
            borderRadius: constants_1.theme.border.radius.small,
            borderStyle: 'solid',
            borderWidth: 1,
            margin: 0,
            padding: 4,
            ...(0, styles_1.inputFontSize)({}),
            ...(props.isDisabled && {
                ...styles_1.DISABLED_STYLES,
                borderColor: 'transparent',
                paddingLeft: 0,
                paddingRight: 0,
            }),
            ...(SINGLE_LINE && {
                backgroundColor: 'transparent',
                border: 'none',
                flexShrink: 0,
                padding: 0,
            }),
        }),
        multiValueLabel: (base, props) => ({
            ...base,
            color: constants_1.Color.DARKEST_BLUE,
            fontWeight: constants_1.theme.font.weight.bold,
            padding: 0,
            ...(0, styles_1.inputFontSize)({}),
            ...(props.isDisabled && {
                ...styles_1.DISABLED_STYLES,
                paddingLeft: 0,
            }),
            ...(SINGLE_LINE && {
                color: constants_1.Color.BLACK,
                fontWeight: constants_1.theme.font.weight.book,
            }),
        }),
        multiValueRemove: (base, props) => ({
            ...base,
            ...(props.isFocused && { backgroundColor: constants_1.Color.LIGHTER_BLUE }),
            color: constants_1.Color.BLUE,
            marginLeft: 4,
            paddingLeft: 2,
            paddingRight: 2,
            ':hover': {
                backgroundColor: constants_1.Color.LIGHTER_BLUE,
            },
            ...(props.isDisabled && { display: 'none' }),
            ...(SINGLE_LINE && {
                display: 'none',
            }),
        }),
        noOptionsMessage: (base) => ({
            ...base,
            color: constants_1.Color.DARKEST_MEDIUM_GREY,
            fontSize: constants_1.theme.font.size[6],
            padding: 16,
        }),
        option: (base, state) => ({
            ...base,
            ...(0, styles_1.dropdownItemStyles)(),
            ...(state.isFocused ? { backgroundColor: constants_1.Color.LIGHTEST_BLUE } : {}),
            ...(state.isSelected ? { backgroundColor: constants_1.Color.LIGHTER_BLUE } : {}),
        }),
        placeholder: (base) => ({
            ...base,
            margin: 0,
            marginLeft: 2,
        }),
        singleValue: (base) => ({
            ...base,
            color: constants_1.Color.BLACK,
            fontSize: 14,
            padding: 2,
        }),
        valueContainer: (base) => ({
            ...base,
            gap: 4,
            padding: 0,
            ...(SINGLE_LINE && {
                flexWrap: 'nowrap',
                gap: 0,
                '& [class*="multiValue"] > :first-child': {
                    overflow: 'visible',
                    padding: 0,
                    '&::after': {
                        color: constants_1.Color.BLACK,
                        content: '", "',
                        whiteSpace: 'pre',
                    },
                },
                '& :nth-last-child(1 of [class*="multiValue"]) > :first-child::after': {
                    content: 'none',
                },
            }),
        }),
    };
};
const InputContainer = styled_1.default.div({
    position: 'relative',
});
const StyledIcon = (0, styled_1.default)(Icon_1.Icon)({
    marginLeft: 8,
    pointerEvents: 'none',
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translate(-50%, -50%)',
});
const StyledOption = (0, styled_1.default)('div', {
    shouldForwardProp: should_forward_prop_1.default,
})((props) => ({
    ...(0, styles_1.dropdownItemStyles)(props),
    alignItems: 'center',
    display: 'flex',
}));
const Option = ({ children, innerRef, innerProps, ...props }) => {
    return ((0, jsx_runtime_1.jsxs)(StyledOption, { active: props.isFocused, ref: innerRef, ...innerProps, children: [children, (0, jsx_runtime_1.jsx)(SelectedIcon_1.SelectedIcon, { selected: props.isSelected })] }));
};
const MultiSelect = (props) => {
    const t = (0, translate_1.useTranslations)();
    const [touched, setTouched] = (0, react_1.useState)(false);
    const validateError = (0, useValidate_1.useValidate)(props.schema, props.required, props.value);
    const errorMessage = props.errorMessage ?? validateError;
    const showError = (touched || props.forceShowError) && !!errorMessage;
    const multiSelect = ((0, jsx_runtime_1.jsxs)(InputContainer, { children: [(0, jsx_runtime_1.jsx)(react_select_1.default, { closeMenuOnSelect: false, components: SINGLE_LINE ? { Option } : undefined, hideSelectedOptions: !SINGLE_LINE, isDisabled: props.disabled, isMulti: true, menuPlacement: "auto", menuPortalTarget: typeof document !== 'undefined' && !props.noPortal
                    ? document.body
                    : null, menuShouldScrollIntoView: false, noOptionsMessage: () => t.form.no_alternatives(), onBlur: () => {
                    setTouched(true);
                }, onChange: (value) => props.onChange(value.map((option) => option.value)), options: props.options, placeholder: props.placeholder ||
                    (props.label ? `${props.label}...` : `${t.form.select_a_value()}...`), required: props.required, styles: createStyles({ invalid: showError }), value: props.options.filter((option) => props.value?.includes(option.value)) }), (0, jsx_runtime_1.jsx)(StyledIcon, { name: "ChevronDownIcon", size: "sm" })] }));
    return props.label ? ((0, jsx_runtime_1.jsxs)(Form_1.Form.Label, { icon: props.labelIcon, required: props.required, text: props.label, children: [multiSelect, (0, jsx_runtime_1.jsx)(ErrorMessage_1.ErrorMessage, { errorMessage: showError ? errorMessage : undefined, reserveSpace: props.reserveErrorSpace })] })) : (multiSelect);
};
exports.MultiSelect = MultiSelect;
